import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, ButtonGroup, Modal } from "react-bootstrap";
import ReactCrop, {
  Crop,
  PercentCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";

import "react-image-crop/dist/ReactCrop.css";

const ASPECT_OPTIONS: { label: string; value?: number }[] = [
  { label: "Livre" },
  { label: "1:1", value: 1 },
  { label: "4:3", value: 4 / 3 },
  { label: "16:9", value: 16 / 9 },
];

const INITIAL_CROP: Crop = { unit: "%", x: 5, y: 5, width: 90, height: 90 };

interface IImageCropModal {
  src?: string;
  onCancel: () => void;
  /** Recebe a imagem recortada e a fração da largura original que sobrou. */
  onConfirm: (file: File, widthFraction: number) => Promise<void>;
}

/** Recorta a imagem na resolução original do arquivo (não na exibida). */
const cropToFile = (img: HTMLImageElement, crop: PercentCrop) => {
  const sx = (crop.x / 100) * img.naturalWidth;
  const sy = (crop.y / 100) * img.naturalHeight;
  const width = Math.max(1, Math.round((crop.width / 100) * img.naturalWidth));
  const height = Math.max(
    1,
    Math.round((crop.height / 100) * img.naturalHeight)
  );

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d")!;
  // O upload já converte tudo para JPEG: fundo branco evita transparência
  // virando preto.
  context.fillStyle = "#FFFFFF";
  context.fillRect(0, 0, width, height);
  context.drawImage(img, sx, sy, width, height, 0, 0, width, height);

  return new Promise<File>((resolve, reject) => {
    // toBlob lança SecurityError se a imagem veio de outra origem sem CORS.
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(
              new File([blob], "imagem-recortada.jpg", { type: "image/jpeg" })
            )
          : reject(new Error("Não foi possível recortar a imagem.")),
      "image/jpeg",
      0.92
    );
  });
};

const ImageCropModal: React.FC<IImageCropModal> = ({
  src,
  onCancel,
  onConfirm,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>(INITIAL_CROP);
  const [percentCrop, setPercentCrop] = useState<PercentCrop>(
    INITIAL_CROP as PercentCrop
  );
  const [aspect, setAspect] = useState<number | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Endereço usado no <img> do recorte: blob: local, que pode ir pro canvas.
  const [cropSrc, setCropSrc] = useState<string>();

  useEffect(() => {
    setCropSrc(undefined);
    if (!src) return;
    setCrop(INITIAL_CROP);
    setPercentCrop(INITIAL_CROP as PercentCrop);
    setAspect(undefined);
    setError(null);
    setSaving(false);

    if (src.startsWith("blob:") || src.startsWith("data:")) {
      setCropSrc(src);
      return;
    }

    // Imagem do servidor: o editor já a carregou sem CORS, e o navegador pode
    // reaproveitar essa resposta do cache, deixando o canvas "contaminado" (o
    // recorte não pode ser exportado). Baixa de novo, sem cache, para um blob:
    // local.
    let cancelled = false;
    let objectUrl: string | undefined;
    fetch(src, { mode: "cors", cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setCropSrc(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        console.log({ err });
        // Geralmente o servidor de arquivos não libera a imagem para edição
        // no navegador (CORS); o arquivo original ainda pode ser recortado.
        setError(
          "Não foi possível carregar esta imagem para recorte. Remova-a e " +
            "insira o arquivo novamente para recortá-lo."
        );
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const changeAspect = (value?: number) => {
    setAspect(value);
    const img = imgRef.current;
    if (!value || !img) return;
    const next = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, value, img.width, img.height),
      img.width,
      img.height
    );
    setCrop(next);
    setPercentCrop(next as PercentCrop);
  };

  const confirm = async () => {
    const img = imgRef.current;
    if (!img || !percentCrop.width || !percentCrop.height) return;
    setSaving(true);
    setError(null);
    try {
      const file = await cropToFile(img, percentCrop);
      await onConfirm(file, percentCrop.width / 100);
    } catch (err) {
      console.log({ err });
      setError(
        "Não foi possível recortar esta imagem. Tente novamente ou insira a imagem de novo."
      );
    }
    setSaving(false);
  };

  return (
    <Modal show={!!src} onHide={onCancel} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Recortar imagem</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex align-items-center mb-2">
          <span className="me-2 text-muted">Proporção:</span>
          <ButtonGroup size="sm">
            {ASPECT_OPTIONS.map(({ label, value }) => (
              <Button
                key={label}
                variant={aspect === value ? "primary" : "light"}
                onClick={() => changeAspect(value)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="text-center">
          {cropSrc && (
            <ReactCrop
              crop={crop}
              aspect={aspect}
              minWidth={20}
              minHeight={20}
              keepSelection
              onChange={(pixel, percent) => {
                setCrop(pixel);
                setPercentCrop(percent);
              }}
            >
              <img
                ref={imgRef}
                src={cropSrc}
                alt="Imagem a recortar"
                style={{ maxHeight: "60vh", maxWidth: "100%" }}
                onError={() =>
                  setError("Não foi possível carregar a imagem para recorte.")
                }
              />
            </ReactCrop>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={confirm}
          disabled={
            saving || !cropSrc || !percentCrop.width || !percentCrop.height
          }
        >
          {saving ? (
            <>
              <i className="mdi mdi-spin mdi-loading" /> Recortando...
            </>
          ) : (
            "Recortar"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropModal;
